/**
 * freshness — Knowledge-node loop-object derivations (FE-1 sprint-241, S6).
 *
 * Verification-Loop Slice 1: every Knowledge Node on the Environment canvas
 * becomes a loop object. This module owns the freshness derivation
 * (FR-S6.1), the SEEDED/BASELINE sentinel detection (FR-S6.4), and the
 * seeded freshness clamp (rule b) — all pure, all driven by the
 * ALREADY-FETCHED graph payload. Zero new fetches.
 *
 * Honesty rules (NFR-3, "no invented recency"):
 *  - missing/invalid `updated_at` → 'unknown', NEVER defaulted to fresh;
 *  - seeded nodes are NEVER 'fresh' (they decay from deployment — there is
 *    no Theatre refresh behind them), clamped fresh → decaying in one place
 *    (`nodeFreshness`).
 */

export type Freshness = 'fresh' | 'decaying' | 'stale' | 'unknown';

// Thresholds are PLACEHOLDER values, fixed at the freshness design
// checkpoint (sdd open-Q 1c — the dedicated visual-treatment handoff has
// not landed). Boundary semantics: ages are compared half-open —
// age < 72h = fresh, 72h ≤ age < 14d = decaying, age ≥ 14d = stale —
// so exactly 72h is DECAYING and exactly 14d is STALE.
const FRESH_MAX_MS = 72 * 60 * 60 * 1000; // 72h
const DECAYING_MAX_MS = 14 * 24 * 60 * 60 * 1000; // 14d

/**
 * Derive freshness from a backend `updated_at` timestamp.
 *
 * `now` is injectable for deterministic tests (defaults to wall clock).
 * A FUTURE timestamp (negative age) is treated as 'fresh' — clock skew
 * between backend and client must not mislabel a just-updated node.
 */
export function deriveFreshness(
  updatedAt: string | null | undefined,
  now: Date = new Date(),
): Freshness {
  if (!updatedAt) return 'unknown';
  const t = Date.parse(updatedAt);
  if (Number.isNaN(t)) return 'unknown';
  const age = now.getTime() - t;
  if (age < FRESH_MAX_MS) return 'fresh'; // includes age < 0 (clock skew)
  if (age < DECAYING_MAX_MS) return 'decaying';
  return 'stale';
}

/** Minimal node shape this module reads — structural subset of `GraphNode`. */
export interface LoopNodeLike {
  updated_at?: string | null;
  metadata?: Record<string, unknown>;
}

/**
 * Seeded-row sentinel: `knowledge_node_seeder.py:39-42` stamps manually
 * seeded rows with `attestation_id === "seed:manual"` (no real theatre /
 * attestation behind them). This helper is THE one place that encodes the
 * rule — graduation (rule d) is simply its negation: a node whose
 * `attestation_id` is replaced by a real attestation id is earned, and the
 * seeded treatment + freshness clamp drop. The sentinel is the source of
 * truth over `verification_state` (the state can flip while the sentinel
 * remains — the node still has no real certificate lineage).
 */
export function isSeededNode(node: LoopNodeLike | null | undefined): boolean {
  return node?.metadata?.attestation_id === 'seed:manual';
}

/**
 * Per-node freshness — `deriveFreshness` plus the seeded clamp (rule b):
 * seeded nodes are NEVER 'fresh'; a seeded node whose timestamp would read
 * fresh renders as 'decaying'. 'unknown' stays 'unknown' (no invented
 * recency for seeds either).
 */
export function nodeFreshness(
  node: LoopNodeLike | null | undefined,
  now: Date = new Date(),
): Freshness {
  const freshness = deriveFreshness(node?.updated_at, now);
  if (freshness === 'fresh' && isSeededNode(node)) return 'decaying';
  return freshness;
}
